import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Mail, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getOnboardingStatus,
  updateOnboardingStep,
  type OnboardingStatusResponse,
} from '@/services/inviteApi';
import { getAndClearNextUrl } from '@/components/auth/LoginPage';
import { CreateOrganizationStep } from './steps/CreateOrganizationStep';
import { JoinOrganizationStep } from './steps/JoinOrganizationStep';
import { ApiKeySetupStep } from './steps/ApiKeySetupStep';
import { WelcomeStep } from './steps/WelcomeStep';

type InviteType = 'site' | 'org' | null;

interface WizardData {
  organization?: { id: string; name: string };
  team?: { id: string; name: string };
  apiKeyConfigured: boolean;
  apiKeySkipped: boolean;
  configuredProviders: string[];
}

const STEPS_SITE = ['Create Organization', 'API Key', 'Get Started'];
const STEPS_ORG = ['Join Team', 'API Key', 'Get Started'];

// Map last_completed_step to wizard step index
function getResumeStep(lastCompletedStep: string | undefined): number {
  if (!lastCompletedStep) return 0;

  switch (lastCompletedStep) {
    case 'org_created':
    case 'org_joined':
      return 1; // Resume at API Key step
    case 'api_key':
      return 2; // Resume at Welcome step
    case 'complete':
      return 0; // Shouldn't happen, but start fresh
    default:
      return 0;
  }
}

export function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, activeTeam, refreshSession, logout, isLoading: authLoading } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [inviteType, setInviteType] = useState<InviteType>(null);
  const [noInviteError, setNoInviteError] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatusResponse | null>(null);
  const [wizardData, setWizardData] = useState<WizardData>({
    apiKeyConfigured: false,
    apiKeySkipped: false,
    configuredProviders: [],
  });

  // Fetch onboarding status
  useEffect(() => {
    async function fetchStatus() {
      try {
        const status = await getOnboardingStatus();
        setOnboardingStatus(status);

        if (!status.needs_onboarding) {
          // User doesn't need onboarding, redirect to home
          navigate('/home');
          return;
        }

        // Check if user needs onboarding but has no valid invite and wasn't previously authorized
        if (status.needs_onboarding && !status.invite_type && !status.last_completed_step) {
          setNoInviteError(true);
        }

        setInviteType(status.invite_type || null);

        // Resume from last completed step if applicable
        const resumeStep = getResumeStep(status.last_completed_step);
        setCurrentStep(resumeStep);
      } catch (err) {
        console.error('Failed to fetch onboarding status:', err);
        // If API fails, show error state
        setNoInviteError(true);
      } finally {
        setIsLoading(false);
      }
    }

    if (!authLoading && user) {
      fetchStatus();
    }
  }, [authLoading, user, navigate]);

  // Get steps based on invite type
  const steps = inviteType === 'org' ? STEPS_ORG : STEPS_SITE;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Step handlers
  const handleOrgCreated = async (data: { organization_id: string; organization_name: string; team_id: string; team_name: string }) => {
    setWizardData(prev => ({
      ...prev,
      organization: { id: data.organization_id, name: data.organization_name },
      team: { id: data.team_id, name: data.team_name },
    }));
    // Note: Backend already set the new JWT cookie in complete_site_onboarding response
    // We have the team ID in wizardData, so we don't need to refresh session here
    // Step tracking is done by the backend in complete_site_onboarding
    setCurrentStep(1);
  };

  const handleOrgJoined = async (data: { organization_id: string; organization_name: string; team_id?: string; team_name?: string }) => {
    setWizardData(prev => ({
      ...prev,
      organization: { id: data.organization_id, name: data.organization_name },
      team: data.team_id ? { id: data.team_id, name: data.team_name || '' } : undefined,
    }));
    // Note: Backend already set the new JWT cookie in accept_org_invite response
    // We have the team ID in wizardData, so we don't need to refresh session here
    // Step tracking is done by the backend in accept_org_invite
    setCurrentStep(1);
  };

  const handleApiKeyComplete = async (skipped: boolean, configuredProviders?: string[]) => {
    setWizardData(prev => ({
      ...prev,
      apiKeyConfigured: !skipped,
      apiKeySkipped: skipped,
      configuredProviders: configuredProviders || [],
    }));

    // Update step tracking
    try {
      await updateOnboardingStep('api_key');
    } catch (err) {
      console.error('Failed to update onboarding step:', err);
      // Continue anyway - don't block user
    }

    setCurrentStep(2);
  };

  const handleComplete = async () => {
    // Mark onboarding as complete
    try {
      await updateOnboardingStep('complete');
    } catch (err) {
      console.error('Failed to mark onboarding complete:', err);
      // Continue anyway
    }

    await refreshSession();
    // Redirect to stored next URL or default to developer page
    const nextUrl = getAndClearNextUrl();
    navigate(nextUrl || '/developer');
  };

  // Loading state
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state: user needs onboarding but has no valid invite and wasn't previously authorized
  if (noInviteError) {
    const userEmail = onboardingStatus?.user_email || user?.email;
    const hasExpiredInvite = onboardingStatus?.has_expired_invite;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="py-8">
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="flex justify-center">
                  <AlertCircle className="h-12 w-12 text-destructive" />
                </div>
                <h2 className="text-xl font-semibold">Account Setup Required</h2>
                <p className="text-sm text-muted-foreground">
                  {hasExpiredInvite
                    ? "Your invite has expired. Please request a new invite from an administrator."
                    : "Your account needs to be set up, but we couldn't find a valid invite. This can happen if your invite was already used or if there was an issue during signup."}
                </p>
              </div>

              <Alert>
                <Mail className="h-4 w-4" />
                <AlertDescription>
                  Please contact an administrator to request a new invite, or try signing in with a different account.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => logout()}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out & Try Different Account
                </Button>
              </div>

              {userEmail && (
                <p className="text-xs text-center text-muted-foreground">
                  Signed in as: {userEmail}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        if (inviteType === 'org') {
          return (
            <JoinOrganizationStep
              inviteCode={onboardingStatus?.invite_code || ''}
              organizationName={onboardingStatus?.organization_name || ''}
              teamName={onboardingStatus?.team_name}
              onComplete={handleOrgJoined}
            />
          );
        }
        return <CreateOrganizationStep onComplete={handleOrgCreated} />;

      case 1:
        // Use wizardData.team?.id if available (just created), otherwise use activeTeam from auth (resuming)
        return (
          <ApiKeySetupStep
            teamId={wizardData.team?.id || activeTeam?.id}
            onComplete={handleApiKeyComplete}
          />
        );

      case 2:
        return (
          <WelcomeStep
            organizationName={wizardData.organization?.name || ''}
            teamName={wizardData.team?.name}
            apiKeyConfigured={wizardData.apiKeyConfigured}
            configuredProviders={wizardData.configuredProviders}
            onComplete={handleComplete}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="space-y-4">
            {/* Progress indicator */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Step {currentStep + 1} of {steps.length}</span>
                <span>{steps[currentStep]}</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Step indicators */}
            <div className="flex justify-center gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full ${
                    index <= currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {renderStep()}
        </CardContent>
      </Card>
    </div>
  );
}

export default OnboardingWizard;
